import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ManaCost from '../components/ManaCost';
import { useManaSymbols } from '../hooks/useManaSymbols';
import DeckImport from '../components/DeckImport';
import './DeckDetailPage.css';
import ContextMenu, { ContextMenuItem } from '../components/ContextMenu';
import AddToWishListModal from '../components/AddToWishListModal';

const API = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;

interface DeckCard {
  deckCardId:      number;
  cardId:          string;
  quantity:        number;
  isCommander:     boolean;
  isSideboard:     boolean;
  name:            string;
  setCode:         string;
  setName:         string;
  collectorNumber: string;
  manaCost:        string;
  typeLine:        string;
  colors:          string;
  rarity:          string;
  reserved:        boolean;
  qtyOwned:        number;
  qtyInAllDecks:   number;
  price:           number | null;
  imageSmall:      string;
  overAllocated:   boolean;
  missing:         boolean;
}

interface Deck {
  deckId:          number;
  name:            string;
  format:          string | null;
  description:     string | null;
  playNotes:       string | null;
  status:          string;
  totalCards:      number;
  mainboardCards:  number;
  sideboardCards:  number;
  estimatedValue:  number;
  overAllocatedCount: number;
  missingCount:    number;
  cards:           DeckCard[];
}

interface InventoryCard {
  cardId:          string;
  name:            string;
  setCode:         string;
  setName:         string;
  collectorNumber: string;
  foil:            boolean;
  frameEffects:    string[];
  borderColor:     string;
  finishes:        string[];
  promoTypes:      string[];
  manaCost:        string;
  typeLine:        string;
  qtyAvailable:    number;
  price:           number | null;
  imageSmall:      string;
  imageNormal:     string;
}

export default function DeckDetailPage() {
  const { deckId }  = useParams<{ deckId: string }>();
  const navigate    = useNavigate();
  const symbols     = useManaSymbols();

  const [deck,        setDeck]        = useState<Deck | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [showAdd,     setShowAdd]     = useState(false);
  const [showImport,  setShowImport]  = useState(false);
  const [addSearch,   setAddSearch]   = useState('');
  const [inventory,   setInventory]   = useState<InventoryCard[]>([]);
  const [invLoading,  setInvLoading]  = useState(false);
  const [addSideboard,setAddSideboard]= useState(false);
  const [editingQty,  setEditingQty]  = useState<number | null>(null);
  const [editQtyVal,  setEditQtyVal]  = useState(1);
  const [popup, setPopup] = useState<{ card: DeckCard | InventoryCard; x: number; y: number } | null>(null);

  // Per-row qty for the add panel — keyed by cardId
  const [addQtys, setAddQtys] = useState<Record<string, number>>({});
  const [contextMenu, setContextMenu] = useState<{
    x: number; y: number; items: ContextMenuItem[];
  } | null>(null);
  const [wishListModal, setWishListModal] = useState<{
    cardId: string; cardName: string; imageUrl?: string | null;
  } | null>(null);

  // Ref to preserve scroll position in add panel after re-search
  const addListRef = useRef<HTMLDivElement>(null);
  const scrollPosRef = useRef<number>(0);

  const fetchDeck = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/decks/${deckId}`);
      setDeck(res.data);
    } catch (e) {
      console.error('Failed to fetch deck:', e);
    } finally {
      setLoading(false);
    }
  }, [deckId]);

  useEffect(() => { fetchDeck(); }, [fetchDeck]);

  const invAbortRef    = useRef<AbortController | null>(null);
  const invDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchInventory = useCallback(async () => {
    if (invAbortRef.current) invAbortRef.current.abort();
    invAbortRef.current = new AbortController();
    const signal = invAbortRef.current.signal;
    if (addListRef.current) scrollPosRef.current = addListRef.current.scrollTop;
    setInvLoading(true);
    try {
      const res = await axios.get(`${API}/decks/inventory`, {
        signal,
        params: {
          q: addSearch,
          ...(deck?.format ? { format: deck.format } : {}),
        }
      });
      if (!signal.aborted) setInventory(res.data);
    } catch (e: any) {
      if (e?.code !== 'ERR_CANCELED') console.error('Failed to fetch inventory:', e);
    } finally {
      if (!signal.aborted) {
        setInvLoading(false);
        requestAnimationFrame(() => {
          if (addListRef.current) addListRef.current.scrollTop = scrollPosRef.current;
        });
      }
    }
  }, [addSearch, deck?.format]);

  useEffect(() => {
    if (!showAdd) return;
    if (invDebounceRef.current) clearTimeout(invDebounceRef.current);
    invDebounceRef.current = setTimeout(() => searchInventory(), 300);
    return () => { if (invDebounceRef.current) clearTimeout(invDebounceRef.current); };
  }, [showAdd, searchInventory]);

  const getAddQty = (cardId: string) => addQtys[cardId] ?? 1;
  const setAddQty = (cardId: string, qty: number) => {
    setAddQtys(prev => ({ ...prev, [cardId]: Math.max(1, qty) }));
  };

  const addCard = async (cardId: string, foil: boolean = false, qty: number = 1, isSideboard: boolean = false) => {
    try {
      await axios.post(`${API}/decks/${deckId}/cards`, {
        cardId, quantity: qty, isSideboard, foil,
      });
      fetchDeck();
      searchInventory();
    } catch (e) {
      console.error('Failed to add card:', e);
    }
  };

  const removeCard = async (deckCardId: number) => {
    try {
      await axios.delete(`${API}/decks/${deckId}/cards/${deckCardId}`);
      fetchDeck();
    } catch (e) {
      console.error('Failed to remove card:', e);
    }
  };

  const saveQty = async (deckCardId: number) => {
    if (editQtyVal < 1) return;
    try {
      await axios.put(`${API}/decks/${deckId}/cards/${deckCardId}`, {
        quantity: editQtyVal,
      });
      setEditingQty(null);
      fetchDeck();
    } catch (e) {
      console.error('Failed to update quantity:', e);
    }
  };

  const goToCard = (cardId: string) => {
    navigate(`/cards/${cardId}`, { state: { from: `/decks/${deckId}` } });
  };

  if (loading) return <div className="deck-detail-loading">Loading deck…</div>;
  if (!deck)   return <div className="deck-detail-loading">Deck not found.</div>;

  const mainboard  = deck.cards.filter(c => !c.isSideboard);
  const sideboard  = deck.cards.filter(c => c.isSideboard);
  const commanders = mainboard.filter(c => c.isCommander);
  const nonCommanders = mainboard.filter(c => !c.isCommander);

  const grouped: Record<string, DeckCard[]> = {};
  for (const card of nonCommanders) {
    const type = card.typeLine?.split('—')[0].trim().split(' ').slice(-1)[0] || 'Other';
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(card);
  }
  const TYPE_ORDER = ['Creature','Planeswalker','Instant','Sorcery','Enchantment','Artifact','Land','Other'];
  const sortedTypes = Object.keys(grouped).sort((a, b) => {
    const ai = TYPE_ORDER.indexOf(a), bi = TYPE_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const handleDeckCardContextMenu = (card: DeckCard, e: React.MouseEvent) => {
    e.preventDefault();
    setPopup(null);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          icon: '🔢',
          label: 'Adjust Quantity',
          onClick: () => { setEditingQty(card.deckCardId); setEditQtyVal(card.quantity); },
        },
        {
          icon: '🔍',
          label: 'View Card Detail',
          onClick: () => goToCard(card.cardId),
        },
        {
          icon: '📋',
          label: 'Add to Wish List',
          onClick: () => setWishListModal({ cardId: card.cardId, cardName: card.name, imageUrl: card.imageSmall || null }),
          divider: true,
        },
        {
          icon: '✕',
          label: 'Remove from Deck',
          onClick: () => removeCard(card.deckCardId),
          danger: true,
        },
      ],
    });
  };

  const handleInvCardContextMenu = (card: InventoryCard, e: React.MouseEvent) => {
    e.preventDefault();
    setPopup(null);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          icon: '🔍',
          label: 'View Card Detail',
          onClick: () => goToCard(card.cardId),
        },
        {
          icon: '📋',
          label: 'Add to Wish List',
          onClick: () => setWishListModal({ cardId: card.cardId, cardName: card.name, imageUrl: card.imageSmall || null }),
        },
      ],
    });
  };

  const renderCard = (card: DeckCard) => (
    <div key={card.deckCardId}
         className={`deck-card-row ${card.overAllocated ? 'over-allocated' : ''} ${card.missing ? 'missing' : ''}`}
         onMouseEnter={e => setPopup({ card, x: e.clientX, y: e.clientY })}
         onMouseLeave={() => setPopup(null)}
         onContextMenu={e => handleDeckCardContextMenu(card, e)}>
      <div className="deck-row-qty">
        {editingQty === card.deckCardId ? (
          <input type="number" min={1} max={99}
                 value={editQtyVal}
                 onChange={e => setEditQtyVal(Number(e.target.value))}
                 onBlur={() => saveQty(card.deckCardId)}
                 onKeyDown={e => { if (e.key === 'Enter') saveQty(card.deckCardId); if (e.key === 'Escape') setEditingQty(null); }}
                 className="deck-qty-input"
                 autoFocus />
        ) : (
          <span className="deck-qty"
                onClick={() => { setEditingQty(card.deckCardId); setEditQtyVal(card.quantity); }}>
            {card.quantity}
          </span>
        )}
      </div>
      <div className="deck-row-name" onClick={() => goToCard(card.cardId)}>
        <span className="deck-row-card-name">{card.name}</span>
        {card.isCommander && <span className="deck-badge-commander">CMD</span>}
        {card.reserved    && <span className="deck-badge-rl">RL</span>}
        {card.overAllocated && <span className="deck-badge-warn" title={`Assigned to ${card.qtyInAllDecks} decks but only own ${card.qtyOwned}`}>⚠ Over</span>}
        {card.missing     && <span className="deck-badge-missing">Missing</span>}
      </div>
      <div className="deck-row-set">
        <span className="deck-row-set-code">{card.setCode?.toUpperCase()}</span>
      </div>
      <div className="deck-row-num">
        <span className="deck-row-collector">{card.collectorNumber || '—'}</span>
      </div>
      <div className="deck-row-mana">
        <ManaCost manaCost={card.manaCost} size={13} />
      </div>
      <div className="deck-row-owned">
        {card.missing
          ? <span className="deck-owned-missing">0</span>
          : <span className={card.overAllocated ? 'deck-owned-warn' : 'deck-owned-ok'}>
              {card.qtyOwned}
            </span>}
      </div>
      <div className="deck-row-price">
        {card.price != null ? `$${card.price.toFixed(2)}` : '—'}
      </div>
      <button className="deck-row-remove" onClick={() => removeCard(card.deckCardId)}>✕</button>
    </div>
  );

  return (
    <div className="deck-detail-page" onMouseLeave={() => setPopup(null)}>

      {/* Hover popup */}
      {popup && !contextMenu && (
        <div style={{ position: 'fixed', left: popup.x + 16,
                      top: Math.max(8, Math.min(popup.y - 100, window.innerHeight - 320)),
                      zIndex: 2000, pointerEvents: 'none' }}>
          {'imageNormal' in popup.card && popup.card.imageNormal
            ? <img src={popup.card.imageNormal} alt={popup.card.name}
                   style={{ width: 260, borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.8)' }} />
            : 'imageSmall' in popup.card && popup.card.imageSmall
              ? <img src={popup.card.imageSmall} alt={popup.card.name}
                     style={{ width: 200, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.8)' }} />
              : null}
        </div>
      )}

      {/* Back + header */}
      <div className="deck-detail-header">
        <button className="deck-back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className="deck-detail-title-row">
          <h1 className="deck-detail-title">{deck.name}</h1>
          {deck.format && (
            <span className="deck-format-badge">
              {deck.format.charAt(0).toUpperCase() + deck.format.slice(1)}
            </span>
          )}
        </div>
        {deck.description && (
          <div className="deck-detail-desc">{deck.description}</div>
        )}
        {deck.playNotes && (
          <details className="deck-play-notes">
            <summary className="deck-play-notes-summary">📖 How to Play</summary>
            <div className="deck-play-notes-body">
              {deck.playNotes.split('\n').map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Stats bar */}
      <div className="deck-stats-bar">
        <div className="deck-stat-pill">
          <span className="dsp-value">{deck.mainboardCards}</span>
          <span className="dsp-label">Mainboard</span>
        </div>
        {deck.sideboardCards > 0 && (
          <div className="deck-stat-pill">
            <span className="dsp-value">{deck.sideboardCards}</span>
            <span className="dsp-label">Sideboard</span>
          </div>
        )}
        <div className="deck-stat-pill">
          <span className="dsp-value">${deck.estimatedValue.toFixed(0)}</span>
          <span className="dsp-label">Est. Value</span>
        </div>
        {deck.overAllocatedCount > 0 && (
          <div className="deck-stat-pill warn">
            <span className="dsp-value">⚠ {deck.overAllocatedCount}</span>
            <span className="dsp-label">Over-allocated</span>
          </div>
        )}
        {deck.missingCount > 0 && (
          <div className="deck-stat-pill missing">
            <span className="dsp-value">{deck.missingCount}</span>
            <span className="dsp-label">Missing</span>
          </div>
        )}
        <button className="deck-add-btn" onClick={() => setShowAdd(true)}>+ Add Cards</button>
        <button className="deck-import-btn" onClick={() => setShowImport(true)}>↑ Import</button>
        <button className="deck-build-btn" onClick={() => navigate(`/decks/${deckId}/build`)}>⊞ Build</button>
      </div>

      {/* Column headers */}
      <div className="deck-col-headers">
        <span className="dch-qty">Qty</span>
        <span className="dch-name">Card</span>
        <span className="dch-set">Set</span>
        <span className="dch-num">#</span>
        <span className="dch-mana">Mana</span>
        <span className="dch-owned">Owned</span>
        <span className="dch-price">Price</span>
        <span className="dch-remove"></span>
      </div>

      {commanders.length > 0 && (
        <div className="deck-section">
          <div className="deck-section-header">Commander ({commanders.length})</div>
          {commanders.map(renderCard)}
        </div>
      )}

      {sortedTypes.map(type => (
        <div key={type} className="deck-section">
          <div className="deck-section-header">
            {type} ({grouped[type].reduce((s, c) => s + c.quantity, 0)})
          </div>
          {grouped[type].map(renderCard)}
        </div>
      ))}

      {sideboard.length > 0 && (
        <div className="deck-section">
          <div className="deck-section-header">
            Sideboard ({sideboard.reduce((s, c) => s + c.quantity, 0)})
          </div>
          {sideboard.map(renderCard)}
        </div>
      )}

      {deck.cards.length === 0 && (
        <div className="deck-empty">No cards yet. Click "Add Cards" to build your deck.</div>
      )}

      {showImport && deck && (
        <DeckImport
          deckId={Number(deckId)}
          deckName={deck.name}
          onDone={() => fetchDeck()}
          onClose={() => setShowImport(false)}
        />
      )}

      {/* Add cards panel */}
      {showAdd && (
        <div className="deck-modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="deck-add-panel" onClick={e => e.stopPropagation()}>
            <div className="deck-add-header">
              <h2>Add Cards</h2>
              <button className="deck-add-close" onClick={() => setShowAdd(false)}>✕</button>
            </div>
            <div className="deck-add-controls">
              <div className="deck-add-search-wrap">
                <input
                  className="deck-add-search"
                  placeholder="Search your collection…"
                  value={addSearch}
                  onChange={e => setAddSearch(e.target.value)}
                  autoFocus
                />
                {addSearch && (
                  <button className="deck-add-search-clear"
                          onClick={() => setAddSearch('')}>✕</button>
                )}
              </div>
              {deck?.format && (
                <div className="deck-add-format-hint">
                  Showing cards legal in {deck.format.charAt(0).toUpperCase() + deck.format.slice(1)}
                </div>
              )}
              <label className="deck-sideboard-toggle">
                <input type="checkbox" checked={addSideboard}
                       onChange={e => setAddSideboard(e.target.checked)} />
                Add to sideboard
              </label>
            </div>
            {invLoading ? (
              <div className="deck-add-loading">Searching…</div>
            ) : inventory.length === 0 ? (
              <div className="deck-add-empty">
                {addSearch ? 'No available cards match your search' : 'No available cards in collection'}
              </div>
            ) : (
              <div className="deck-add-list" ref={addListRef}>
                {inventory.map(card => {
                  // Build variant badges
                  const badges: string[] = [];
                  if (card.foil) badges.push('Foil');
                  if (card.borderColor === 'borderless') badges.push('Borderless');
                  if (card.frameEffects?.length) {
                    card.frameEffects.forEach(fe => {
                      const f = fe.trim();
                      if      (f === 'extendedart')   badges.push('Extended Art');
                      else if (f === 'showcase')      badges.push('Showcase');
                      else if (f === 'etched')        badges.push('Etched');
                      else if (f === 'inverted')      badges.push('Inverted');
                      else if (f === 'colorshifted')  badges.push('Colorshifted');
                      else if (f === 'shatteredglass')badges.push('Shattered Glass');
                      else if (f === 'fullart')       badges.push('Full Art');
                    });
                  }
                  // Foil-only finish
                  if (card.finishes?.includes('foil') && !card.finishes?.includes('nonfoil')) {
                    badges.push('Foil Only');
                  }
                  // Promo type badges
                  const PROMO_LABELS: Record<string,string> = {
                    surgefoil:'Surge Foil', galaxyfoil:'Galaxy Foil', textured:'Textured Foil',
                    halofoil:'Halo Foil', ripplefoil:'Ripple Foil', rainbowfoil:'Rainbow Foil',
                    doublerainbow:'Double Rainbow', oilslick:'Oil Slick', neonink:'Neon Ink',
                    serialized:'Serialized', gilded:'Gilded', raisedfoil:'Raised Foil',
                    stepandcompleat:'Step & Compleat', confettifoil:'Confetti Foil',
                    silverfoil:'Silver Foil', fracturefoil:'Fracture Foil',
                    manafoil:'Mana Foil', invisibleink:'Invisible Ink',
                    dazzlefoil:'Dazzle Foil', embossed:'Embossed', magnified:'Magnified',
                  };
                  card.promoTypes?.forEach(pt => {
                    if (PROMO_LABELS[pt]) badges.push(PROMO_LABELS[pt]);
                  });
                  const rowKey = `${card.cardId}-${card.foil}`;
                  return (
                    <div key={rowKey} className="deck-add-row"
                         onMouseEnter={e => { if (!contextMenu) setPopup({ card, x: e.clientX, y: e.clientY }); }}
                         onMouseLeave={() => setPopup(null)}
                         onContextMenu={e => handleInvCardContextMenu(card, e)}>
                      <div className="deck-add-info">
                        <div className="deck-add-name">{card.name}</div>
                        <div className="deck-add-meta">
                          <span className="deck-add-set">{card.setCode?.toUpperCase()}</span>
                          <span className="deck-add-collector">#{card.collectorNumber}</span>
                          <ManaCost manaCost={card.manaCost} size={12} />
                          {badges.map(b => (
                            <span key={b} className={`deck-add-badge ${b === 'Foil' ? 'foil' : ''}`}>{b}</span>
                          ))}
                          <span className="deck-add-avail">×{card.qtyAvailable} avail</span>
                        </div>
                      </div>
                      <div className="deck-add-price">
                        {card.price != null ? `$${card.price.toFixed(2)}` : '—'}
                      </div>
                      <div className="deck-add-qty-wrap">
                        <button className="deck-add-qty-btn"
                                onClick={() => setAddQty(rowKey, getAddQty(rowKey) - 1)}>−</button>
                        <input
                          type="number"
                          className="deck-add-qty-input"
                          min={1}
                          max={card.qtyAvailable}
                          value={getAddQty(rowKey)}
                          onChange={e => setAddQty(rowKey, Number(e.target.value) || 1)}
                          onClick={e => e.stopPropagation()}
                        />
                        <button className="deck-add-qty-btn"
                                onClick={() => setAddQty(rowKey, getAddQty(rowKey) + 1)}>+</button>
                      </div>
                      <button className="deck-add-card-btn"
                              onClick={() => addCard(card.cardId, card.foil, getAddQty(rowKey), addSideboard)}>
                        Add
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={() => setContextMenu(null)}
        />
      )}
      {wishListModal && (
        <AddToWishListModal
          cardId={wishListModal.cardId}
          cardName={wishListModal.cardName}
          imageUrl={wishListModal.imageUrl}
          onDone={() => setWishListModal(null)}
          onCancel={() => setWishListModal(null)}
        />
      )}
    </div>
  );
}
